/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BuilderNavigationService, FilterNames, FilterRestService } from '@gh/builder-shared';
import {
  AelEvents,
  BuilderPermissionSet,
  getAelEventsArray,
  ScheduleType,
  UserListItemDto,
  ScheduleDetailsDto,
} from '@gh/builder-shared-data';
import {
  cellValueFormatter,
  rendererIconByName,
  truthyCellRenderer,
} from '@gh/common-internal-shared';
import {
  AppRouter,
  DateTimeTypes,
  EnumDictionary,
  filterableForm,
  FilterableFormState,
  FormStateBuilder,
  getAllNewSelectedEntities,
  getRouteInitData,
  LocalizationBusinessService,
  UserContext,
} from '@gh/common-shared';
import { ID } from 'common-shared-data/common';
import { filterTracker, RouteDataService, SlicedDataSource } from '@gh/core-data';
import {
  AutocompleteLoadMoreEvent,
  DialogService,
  NotificationService,
  WaitingService,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import {
  buildDateTimeColumn,
  cursorTracker,
  GridDefinitionFactory,
  LayoutBasedGridDefinition,
  newGridPaginationCursor,
} from '@gh/core-ui-grid';
import { filter$, map$, switchMap$, debounceTime$, skip$, takeUntil$ } from '@gh/rx/operators';
import { omit, pick } from 'lodash';
import { Subject } from 'rxjs';
import {
  DATE_FILTER,
  ScheduleFinderDialogConfig,
} from '../../resources/schedule-finder-business.service';
import { ScheduleFinderListItem } from '../../resources/schedule-finder.model';
import {
  JobScheduleCreateDialogConfig,
  ScheduleListBusinessService,
} from '../../resources/schedule-list-business.service';
import {
  MaintenanceScheduleFilter,
  MaintenanceScheduleListItem,
} from '../../resources/schedule-list.model';
import { jobScheduleCreateDialogRoute } from '../job-schedule-create-dialog/job-schedule-create-dialog.component';
import { scheduleFinderDialogRoute } from '../schedule-finder-dialog/schedule-finder-dialog.route';
import { TranslationService } from '@gh/core-mls';
import { of$ } from '@gh/rx';

const FILTER_PERSIST_DEBOUNCE_MS = 300;

@Component({
  standalone: false,
  selector: 'gh-maintenance-schedule-list',
  templateUrl: 'maintenance-schedule-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MaintenanceScheduleListComponent implements OnInit, OnDestroy {
  gridDefinition: LayoutBasedGridDefinition;
  cursor = newGridPaginationCursor();
  dataSource: SlicedDataSource<MaintenanceScheduleListItem>;
  form: FilterableFormState<MaintenanceScheduleFilter>;
  filter: any;
  supervisors: UserListItemDto[];
  supervisorsConst = [
    {userName: this.translations.get('lbl_all'), userId: 'ALL'},
    {userName: this.translations.get('lbl_unassigned'), userId: 'UNASSIGNED'},
  ];
  dateFilter = DATE_FILTER;
  private readonly destroy$ = new Subject<void>();
  private scheduleTypeDict: EnumDictionary<ScheduleType>;

  get isShowMyJobsDisabled(): boolean {
    if (!this.form) {
      return true;
    }
    return this.form.getControl('supervisorId').value === this.userContext.data.id;
  }

  constructor(private route: ActivatedRoute,
              private router: AppRouter,
              private userContext: UserContext,
              private routeData: RouteDataService,
              private formStateBuilder: FormStateBuilder,
              private dialogs: DialogService,
              private notifications: NotificationService,
              private waitings: WaitingService,
              private filterService: FilterRestService,
              private navigations: BuilderNavigationService,
              private gridDefinitionFactory: GridDefinitionFactory,
              private translations: TranslationService,
              private localizationBusinessService: LocalizationBusinessService,
              private business: ScheduleListBusinessService,
              private changeDetectorRef: ChangeDetectorRef) {
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    const { filter: initFilter, supervisors, gridLayout, scheduleTypeDict } = getRouteInitData(this.route);
    this.supervisors = [
      ...this.supervisorsConst,
      ...supervisors];
    this.scheduleTypeDict = scheduleTypeDict;
    this.gridDefinition = this.gridDefinitionFactory.buildLayoutBased(
      {
        jobNumber: {
          // list always gets sorted from BE
          comparator: () => 0,
        },
        startDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
        endDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
        isTodoExist: { cellRenderer: truthyCellRenderer(rendererIconByName('done')) },
        duration: {
          valueFormatter: cellValueFormatter<number>((duration) =>
            this.localizationBusinessService.formatHoursDuration(duration)),
        },
      },
      {
        getRowId: (params) => String(params.data.scheduleId)
      });
    this.gridDefinition.bind(gridLayout);

    let supervisor = this.supervisorsConst.find((item) => item.userId === 'ALL');
    if (initFilter.supervisorId) {
      supervisor = this.supervisors.find((user) => user.userId === initFilter.supervisorId);
    } else if (initFilter.showSupervisors === 'UNASSIGNED') {
      supervisor = this.supervisorsConst.find((item) => item.userId === initFilter.showSupervisors);
    }
    this.form = filterableForm(this.formStateBuilder.buildPlain({
      value: {
        ...initFilter,
        supervisor,
        handoverDate: initFilter.handoverDate || 'ALL',
        completedDate: initFilter.completedDate || 'ALL',
      },
      children: {
        showSupervisors: void 0,
        supervisorId: void 0,
        supervisor: void 0,
        searchString: void 0,
        showFinalized: void 0,
        completedDate: void 0,
        handoverDate: void 0,
        showActive: void 0,
        showMyJobs: void 0,
        showCompleted: void 0,
      },
    }));

    this.updateShowMyJobsDisabledState();

    this.form.getControl('supervisorId').valueChanges.pipe(
      takeUntil$(this.destroy$),
    ).subscribe(() => {
      this.updateShowMyJobsDisabledState();
    });

    const tracker = this.routeData
      .tracker(filterTracker(this.form))
      .withEffects(cursorTracker(this.cursor));
    tracker.listen(this.route);
    tracker.filterChanges.pipe(
      takeUntil$(this.destroy$),
    ).subscribe(([filter]) => {
      this.filter = this.getFilterValue(filter);
      this.dataSource = this.business.findAllMaintenanceSchedulesAsDataSource(this.filter);
      this.changeDetectorRef.markForCheck();
    });
    tracker.filterChanges.pipe(
      debounceTime$(FILTER_PERSIST_DEBOUNCE_MS),
      skip$(1),
      switchMap$(([filter]) => this.filterService.update(
        FilterNames.maintenanceSchedule,
        this.getFilterValue(filter),
      )),
      takeUntil$(this.destroy$),
    ).subscribe({ error: this.notifications.processAll });
  }

  getFilterValue(filter: any): any {
    const excludedPaths = ['supervisor'];
    if (!filter.supervisorId || filter.supervisorId === 'UNASSIGNED' || filter.supervisorId === 'ALL') {
      excludedPaths.push('supervisorId');
    }
    if (!filter.showSupervisors) {
      excludedPaths.push('showSupervisors');
    }
    return omit(filter, excludedPaths);
  }

  onCreate(): void {
    this.dialogs.openWithInput(jobScheduleCreateDialogRoute, <JobScheduleCreateDialogConfig>{
      type: ScheduleType.Maintenance,
      helpId: 'schedules/warranty/details',
    }).afterClosed().pipe(
      filter$(Boolean))
      .subscribe((init: { jobId: ID; templateId: ID }) => {
        this.business.init(init.jobId, init.templateId, ScheduleType.Maintenance)
          .subscribe(
            (scheduleDetails: ScheduleDetailsDto) => {
              if (scheduleDetails.errorMessage && scheduleDetails.errorMessage.trim() !== "") {
                this.notifications.warning("msg_cyclic_depedency", { issues: scheduleDetails.errorMessage });
              } else {
                this.router.navigateToChild(['schedules/maintenance/new/gantt-chart'], {
                  queryParams: pick(init, 'jobId', 'templateId'),
                });
              }
            },
            this.notifications.processAll,
          );
      });
  }

  onSearch(): void {
    this.form.emitFilter();
  }

  goToAel(schedule?: MaintenanceScheduleListItem): void {
    this.navigations.goToAel({
      label: 'lbl_maintenance_schedules',
      entityType: 'JOB',
      entityId: schedule ? schedule.jobId : void 0,
      eventTypeIds: getAelEventsArray(AelEvents.Job.MaintenanceSchedule),
    });
  }

  onOpenCombinedGanttChart(): void {
    this.dialogs.openWithInput(scheduleFinderDialogRoute, <ScheduleFinderDialogConfig>{
      type: ScheduleType.Maintenance,
      multiselection: true,
    }).afterClosed().pipe(
      filter$(Boolean),
      map$(getAllNewSelectedEntities))
      .subscribe((items: ScheduleFinderListItem[]) => {
        this.router.navigateToChild(['schedules/maintenance/combined/gantt-chart'], {
          queryParams: { scheduleIds: items.map((item) => item.scheduleId).join() },
        });
      });
  }

  onOpen(schedule: MaintenanceScheduleListItem): void {
    if (this.userContext.hasPermission(BuilderPermissionSet.Schedules.Maintenance.EDIT_VIEW)) {
      this.router.navigateToChild(['schedules/maintenance', schedule.scheduleId, 'gantt-chart']);
    }
  }

  onRemove(schedule: MaintenanceScheduleListItem): void {
    this.dialogs.confirmation(yesNoConfirmation('msg_sch_delete', {
      schType: this.scheduleTypeDict.get(ScheduleType.Maintenance),
      jobNumber: schedule.jobNumber,
    })).pipe(
      filter$(yesAnswer),
      switchMap$(() => this.business.removeMaintenanceSchedule(schedule).pipe(this.waitings.operator())))
      .subscribe(
        () => this.loadData(),
        this.notifications.processAll);
  }
  loadSupervisors(event: AutocompleteLoadMoreEvent): void {
    event.load(of$(this.supervisors.filter((user) => user.userName.toLowerCase().includes(event.query.filter?.toLowerCase()))));
  }

  onSupervisorSelected(supervisor: UserListItemDto): void {
    if (!supervisor) {
      this.form.getControl('supervisorId').setValue('');
      this.form.getControl('showSupervisors').setValue('');
    } else if (supervisor.userId === 'UNASSIGNED') {
      this.form.getControl('supervisorId').setValue('UNASSIGNED');
      this.form.getControl('showSupervisors').setValue('UNASSIGNED');
    } else if (supervisor.userId === 'ALL') {
      this.form.getControl('supervisorId').setValue('ALL');
      this.form.getControl('showSupervisors').setValue('ALL');
    } else if (!supervisor || supervisor.userId !== this.form.getControl('supervisorId').value) {
      if (this.userContext.data.id === supervisor.userId) {
        this.form.getControl('showMyJobs').setValue('false');
      }
      this.form.getControl('supervisorId').setValue(supervisor.userId);
      this.form.getControl('showSupervisors').setValue('ID');
    }
    this.onSearch();
  }

  private loadData(): void {
    this.dataSource = this.business.findAllMaintenanceSchedulesAsDataSource(this.filter);
  }

  private updateShowMyJobsDisabledState(): void {
    const showMyJobsControl = this.form.getControl('showMyJobs');
    if (this.isShowMyJobsDisabled) {
      showMyJobsControl.disable();
    } else {
      showMyJobsControl.enable();
    }
    this.changeDetectorRef.markForCheck();
  }
}
